import type { ClassFilter, Analysis } from '@teacher-shared/analysis';
import { BUCKETS, BUCKET_LABEL } from '@/lib/teaching/slice4';
import { TeachingPage, BUCKET_FILL, ClassSwitcher, useClass, WithData, useAnalysis, AssessmentSwitcher, ProvisionalBanner, breadcrumbOf, Card, RateBar, StatusChip, perfFill, INK, pct } from '@/components/teaching/ui';
import { KpiRow, KpiCard } from '@/components/teaching/kit';

function Counts({ A, cls }: { A: Analysis; cls: ClassFilter }) {
  const all = A.summariesIn(cls);
  const done = all.filter((s) => s.student.status === 'completed');
  const complete = done.filter((s) => s.complete);
  const avg = complete.length ? complete.reduce((a, s) => a + (s.marks ?? 0), 0) / complete.length : null;
  const tiles: [string, string | number, string?][] = [
    ['Assessed', done.length],
    ['Absent', all.filter((s) => s.student.status === 'absent').length],
    ['Catch-up pending', all.filter((s) => s.student.status === 'catchup').length],
    ['Need support', A.struggling(cls).length],
    ['Average (complete papers)', avg === null ? '-' : `${avg.toFixed(1)}/${A.total}`],
  ];
  return (
    <KpiRow cols={5}>
      {tiles.map(([k, v]) => <KpiCard key={k} value={v} label={k} />)}
    </KpiRow>
  );
}

function Distribution({ A, cls }: { A: Analysis; cls: ClassFilter }) {
  const done = A.summariesIn(cls).filter((s) => s.student.status === 'completed');
  const complete = done.filter((s) => s.complete);
  const nb = Math.min(5, A.total + 1);
  const step = (A.total + 1) / nb;
  const bands = Array.from({ length: nb }, (_, i) => ({ lo: Math.ceil(i * step), hi: i === nb - 1 ? A.total : Math.ceil((i + 1) * step) - 1 }));
  const counts = bands.map((b) => complete.filter((s) => s.marks !== null && s.marks >= b.lo && s.marks <= b.hi).length);
  const max = Math.max(1, ...counts);
  // V3: the shared distribution; a judgement needing re-check is not a level.
  const dist = A.levelDistribution(cls);
  const levels = BUCKETS.map((b) => dist.buckets[b]);
  const lmax = Math.max(1, ...levels);
  const reviewN = done.filter((s) => s.student.myp?.needsReview || s.student.notAssessable?.needsReview).length;
  return (
    <div class="grid md:grid-cols-2 gap-3">
      <Card title="Raw scores" sub={`Complete papers only (${complete.length}). ${done.length - complete.length} partial paper(s) left out.`}>
        <div class="flex items-end gap-2 h-[120px]">
          {bands.map((b, i) => (
            <div key={b.lo} class="flex-1 flex flex-col items-center justify-end h-full">
              <div class="text-[11px] tabular-nums text-[var(--color-text-muted)]">{counts[i]}</div>
              <div class="w-full rounded-t" style={{ height: `${(counts[i] / max) * 85}%`, background: perfFill(bands.length > 1 ? i / (bands.length - 1) : 1) }} />
              <div class="text-[10px] text-[var(--color-text-faint)] mt-1 tabular-nums">{b.lo}-{b.hi}</div>
            </div>
          ))}
        </div>
      </Card>
      <Card title={`MYP levels (Criterion ${A.assessment.criterion})`} sub={`Levels you confirmed, out of ${dist.population} assessed students. A level is never worked out from a percentage.${reviewN ? ` ${reviewN} need re-check.` : ''}`}>
        <div class="flex items-end gap-1.5 h-[120px]" data-level-distribution={levels.join(',')}>
          {levels.map((n, i) => (
            <div key={BUCKETS[i]} class="flex-1 flex flex-col items-center justify-end h-full">
              <div class="text-[11px] tabular-nums text-[var(--color-text-muted)]">{n}</div>
              <div class="w-full rounded-t" style={{ height: `${(n / lmax) * 85}%`, background: BUCKET_FILL[BUCKETS[i]] }} />
              <div class="text-[10px] text-[var(--color-text-faint)] mt-1 text-center leading-tight">{BUCKET_LABEL[BUCKETS[i]]}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function Compare({ A }: { A: Analysis }) {
  const topics = Array.from(new Set(A.items.map((i) => i.topic)));
  const byClass = A.classes.map((c) => ({ c, rates: Object.fromEntries(A.bucketRates('topic', c).map((b) => [b.label, b.r])) }));
  return (
    <Card title="Classes side by side" sub="Topic mastery % (marked answers only)">
      <div class="overflow-x-auto">
        <table class="w-full text-[12px] border-collapse">
          <thead>
            <tr>
              <th class="text-left py-1.5 pr-3 font-medium text-[var(--color-text-muted)]">Topic</th>
              {A.classes.map((c) => <th key={c} class="px-1 py-1.5 font-medium text-[var(--color-text-muted)] min-w-[64px]">{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {topics.map((t) => (
              <tr key={t} class="border-t border-[var(--color-border)]">
                <td class="py-1 pr-3 text-[var(--color-text)] whitespace-nowrap">{t}</td>
                {byClass.map(({ c, rates }) => {
                  const r = rates[t];
                  const enough = r && r.n >= 10;
                  return (
                    <td key={c} class="p-0.5">
                      <div class="rounded text-center py-1 tabular-nums" style={{ background: enough ? perfFill(r.rate) : 'transparent', color: enough ? INK : 'var(--color-text-faint)' }}>
                        {enough ? pct(r.rate) : 'n/a'}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export function Overview() {
  return <WithData state={useAnalysis()} title="Class overview">{({ a, A }) => <OverviewView a={a} A={A} />}</WithData>;
}

function OverviewView({ a, A }: { a: string; A: Analysis }) {
  const [cls, setCls] = useClass(A.classes);
  const as = A.assessment;
  const allApproved = A.approval.of > 0 && A.approval.fully === A.approval.of;
  return (
    <TeachingPage title="Class overview" breadcrumb={breadcrumbOf(A)} banner={<ProvisionalBanner A={A} />} actions={<><AssessmentSwitcher current={a} /><ClassSwitcher value={cls} onChange={setCls} classes={A.classes} /></>}>
      <div class="flex flex-col gap-3">
        <div class="flex flex-wrap items-center gap-2 text-[12px] text-[var(--color-text-muted)]">
          <span>Criterion {as.criterion}</span>{as.sat_on && <><span>·</span><span>Sat {as.sat_on}</span></>}<span>·</span>
          {allApproved ? <><StatusChip status="completed" /><span>every mark approved by you</span></> : <span>{A.approval.fully} of {A.approval.of} papers fully marked</span>}
        </div>
        <Counts A={A} cls={cls} />
        <Distribution A={A} cls={cls} />
        <div class="grid md:grid-cols-2 gap-3">
          <Card title="By topic" sub="Mastery % · mastered / answers">
            {A.bucketRates('topic', cls).map((b) => <RateBar key={b.label} label={b.label} rate={b.r.rate} n={b.r.n} mastered={b.r.mastered} />)}
          </Card>
          <div class="flex flex-col gap-3">
            <Card title="By MYP strand" sub="Mastery %">
              {A.bucketRates('strand', cls).map((b) => <RateBar key={b.label} label={b.label} rate={b.r.rate} n={b.r.n} mastered={b.r.mastered} />)}
            </Card>
            <Card title="By command term" sub="Knowledge gap or answer-writing gap?">
              {A.bucketRates('command', cls).map((b) => <RateBar key={b.label} label={b.label} rate={b.r.rate} n={b.r.n} mastered={b.r.mastered} />)}
            </Card>
          </div>
        </div>
        {cls === 'all' && A.classes.length > 1 && <Compare A={A} />}
      </div>
    </TeachingPage>
  );
}
